import React from 'react';
import { DollarSign, Calculator } from 'lucide-react';
import '../styles/WaitlistForm.css';

const ResultsDisplay = ({ results, onHome }) => {
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const downPaymentPercentage = ((results.downPayment / results.maxHomePrice) * 100).toFixed(1);

  return (
    <div className="affordability-results-container results-page-container">
      <div className="affordability-results-header">
      </div>

      <div className="affordability-results-main">
        <div className="affordability-results-card shadow-lg animate-scale-in">
          <div className="card-header bg-gradient-to-r from-blue-600 to-green-600">
            <h3 className="card-title">
              <DollarSign className="card-icon" />
              Max Home Price
            </h3>
          </div>
          <div className="card-content">
            <div className="affordability-results-value">{formatCurrency(results.maxHomePrice)}</div>
            <div className="affordability-results-desc">
              Based on {results.dtiRatio.toFixed(1)}% debt-to-income ratio
              <br />
              Credit Score: {results.creditScore}
            </div>
          </div>
        </div>

        <div className="affordability-results-card shadow-lg animate-scale-in">
           <div className="card-header bg-gradient-to-r from-green-600 to-blue-600">
            <h3 className="card-title">
              <Calculator className="card-icon" />
              Monthly Payment
            </h3>
          </div>
          <div className="card-content">
            <div className="affordability-results-value">{formatCurrency(results.monthlyPayment)}</div>
            <div className="affordability-results-desc">Including all monthly expenses</div>
          </div>
        </div>
      </div>

      <div className="breakdown-section">
        <div className="affordability-breakdown-card">
          <div className="card-header">
            <h3 className="card-title">Loan Details</h3>
          </div>
          <div className="card-content">
            <div className="affordability-breakdown-row">
              <span>Loan Amount:</span>
              <span>{formatCurrency(results.maxLoanAmount)}</span>
            </div>
            <div className="affordability-breakdown-row">
              <span>Down Payment:</span>
              <span>{formatCurrency(results.downPayment)} ({downPaymentPercentage}%)</span>
            </div>
            <div className="affordability-breakdown-row">
              <span>Interest Rate:</span>
              <span>
                {results.interestRate?.toFixed(2) || '6.50'}%
                <span className={`rate-source-indicator ${results.rateSource === 'Auto-Estimated' ? 'rate-source-auto' : 'rate-source-manual'}`}>
                  {results.rateSource || 'Auto-Estimated'}
                </span>
              </span>
            </div>
            <div className="affordability-breakdown-row">
              <span>Principal & Interest:</span>
              <span>{formatCurrency(results.principalInterest)}</span>
            </div>
          </div>
        </div>

        <div className="affordability-breakdown-card">
          <div className="card-header">
            <h3 className="card-title">Monthly Expenses</h3>
          </div>
          <div className="card-content">
            <div className="affordability-breakdown-row">
              <span>Property Taxes</span>
              <span>
                {formatCurrency(results.propertyTaxes)}
                {results.wereEstimated && <span className="estimated-label"> (est.)</span>}
              </span>
            </div>
            <div className="affordability-breakdown-row">
              <span>Insurance</span>
              <span>
                {formatCurrency(results.insurance)}
                {results.wereEstimated && <span className="estimated-label"> (est.)</span>}
              </span>
            </div>
            <div className="affordability-breakdown-row">
              <span>HOA Fees</span>
              <span>
                {formatCurrency(results.hoaFees)}
                {results.wereEstimated && <span className="estimated-label"> (est.)</span>}
              </span>
            </div>
            <div className="affordability-breakdown-row">
              <span>Utilities</span>
              <span>
                {formatCurrency(results.utilities)}
                {results.wereEstimated && <span className="estimated-label"> (est.)</span>}
              </span>
            </div>
          </div>
        </div>

        <div className="affordability-breakdown-card">
          <div className="card-header">
            <h3 className="card-title">Income & Debt</h3>
          </div>
          <div className="card-content">
            <div className="affordability-breakdown-row">
              <span>Monthly Income:</span>
              <span>{formatCurrency(results.totalMonthlyIncome)}</span>
            </div>
            <div className="affordability-breakdown-row">
              <span>Monthly Debt:</span>
              <span>{formatCurrency(results.totalMonthlyDebt)}</span>
            </div>
            <div className="affordability-breakdown-row">
              <span>Debt-to-Income Ratio:</span>
              <span>{results.dtiRatio.toFixed(1)}%</span>
            </div>
          </div>
        </div>
      </div>

      <div className="affordability-disclaimer">
        <strong>Disclaimer:</strong> This calculator provides estimates based on standard lending criteria. 
        Actual loan approval depends on various factors including credit score, employment history, and lender requirements. 
        Consult with qualified mortgage professionals for personalized advice.
      </div>
    </div>
  );
};

export default ResultsDisplay; 